import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView, trackProductPageView } from "@/lib/analytics";

import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { ProductGateway } from "@/components/site/ProductGateway";
import { CorporateStrengths } from "@/components/site/CorporateStrengths";
import { ProductPageHero } from "@/components/site/ProductPageHero";
import { GalvalumPage } from "@/components/site/GalvalumPage";
import { KitchenEquipmentPage } from "@/components/site/KitchenEquipmentPage";
import { ElectronicsPage } from "@/components/site/ElectronicsPage";
import { Products } from "@/components/site/Products";
import { UVProtection } from "@/components/site/UVProtection";
import { Applications } from "@/components/site/Applications";
import { Gallery } from "@/components/site/Gallery";
import { Testimonials } from "@/components/site/Testimonials";
import { Partnership } from "@/components/site/Partnership";
import { CompanyProfile } from "@/components/site/CompanyProfile";
import { SalesTeam } from "@/components/site/SalesTeam";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { SiteMotion } from "@/components/site/SiteMotion";
import { PremiumExperience } from "@/components/site/PremiumExperience";

const SITE_URL = "https://www.animaxputralancar.com";
const SITE_NAME = "Animax Putra Lancar";

const PAGE_SEO = {
  "/": {
    title: "PT ANIMAX PUTRA LANCAR",
    description:
      "PT Animax Putra Lancar menyediakan acrylic sheet, baja ringan, galvalum, kitchen equipment, dan elektronik untuk kebutuhan proyek serta industri.",
  },
  "/produk/animax-acrylic-sheet": {
    title: "Acrylic Sheet | PT APL",
    description:
      "Acrylic sheet Animax untuk signage, display, interior, furniture, dekorasi, dan kebutuhan proyek. Melayani kebutuhan dari Mojokerto ke seluruh Indonesia.",
    productName: "Acrylic",
  },
  "/produk/baja-ringan-galvalum": {
    title: "Baja Ringan & Galvalum | PT APL",
    description:
      "Baja ringan, galvalum, roofing, dan material pendukung untuk atap, struktur ringan, hunian, gudang, dan berbagai kebutuhan proyek.",
    productName: "Galvalum",
  },
  "/produk/kitchen-equipment": {
    title: "Peralatan Dapur | PT APL",
    description:
      "Kitchen equipment AMEX untuk dapur komersial: dishwasher, rice steamer, dryer, kuali range, foodtray, cold storage, dan perlengkapan stainless steel.",
    productName: "Kitchen Equipment",
  },
  "/produk/elektronik": {
    title: "AMEX Smart TV | PT APL",
    description: "AMEX Modern Smart TV ukuran 32 hingga 65 inch dengan Android 11, Full HD hingga 4K, serta konektivitas WiFi dan LAN.",
    productName: "Elektronik",
  },
  "/profil-perusahaan": {
    title: "Profil Perusahaan",
    description:
      "Profil PT Animax Putra Lancar, perusahaan penyedia material proyek, retail, display, interior, dan industri yang berbasis di Mojokerto.",
  },
  "/kontak": {
    title: "Kontak",
    description:
      "Hubungi PT Animax Putra Lancar melalui WhatsApp, telepon, email, atau tim sales untuk kebutuhan material proyek dan industri.",
  },
};

const setMeta = (selector, attr, value) => {
  const tag = document.head.querySelector(selector);
  if (tag) tag.setAttribute(attr, value);
};

const PageMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPathname = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
    const seo = PAGE_SEO[normalizedPathname] || PAGE_SEO["/"];
    const canonical = `${SITE_URL}${normalizedPathname === "/" ? "/" : `${normalizedPathname}/`}`;

    document.title = seo.title;
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[name="application-name"]', "content", SITE_NAME);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
  }, [pathname]);

  return null;
};

const ScrollToTarget = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const pagePath = `${pathname}${hash || ""}`;
    const seo = PAGE_SEO[pathname];

    trackPageView(pagePath);
    if (!hash && seo?.productName) {
      trackProductPageView(seo.productName, pathname);
    }

    if (hash) {
      window.setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 0);
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="home-page">
      <Navbar />
      <main>
        <Hero />
        <ProductGateway />
        <CorporateStrengths compact />
        <Testimonials />
        <Partnership />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const AnimaxAcrylicPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="animax-acrylic-page">
      <Navbar />
      <main>
        <ProductPageHero />
        <Products />
        <UVProtection />
        <Applications />
        <Gallery />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const BajaRinganGalvalumPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="baja-ringan-galvalum-page">
      <Navbar />
      <main>
        <GalvalumPage />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const KitchenEquipmentProductPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="kitchen-equipment-product-page">
      <Navbar />
      <KitchenEquipmentPage />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const ElectronicsProductPage = () => (
  <div className="min-h-screen bg-background text-foreground" data-testid="electronics-product-page">
    <Navbar />
    <ElectronicsPage />
    <Footer />
    <FloatingWhatsApp />
  </div>
);

const CompanyProfilePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="company-profile-page">
      <Navbar />
      <main>
        <CompanyProfile />
        <CorporateStrengths compact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="contact-page">
      <Navbar />
      <main>
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageMetadata />
        <ScrollToTarget />
        <SiteMotion />
        <PremiumExperience />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/produk/animax-acrylic-sheet" element={<AnimaxAcrylicPage />} />
          <Route path="/produk/baja-ringan-galvalum" element={<BajaRinganGalvalumPage />} />
          <Route path="/produk/kitchen-equipment" element={<KitchenEquipmentProductPage />} />
          <Route path="/produk/elektronik" element={<ElectronicsProductPage />} />
          <Route path="/profil-perusahaan" element={<CompanyProfilePage />} />
          <Route path="/kontak" element={<ContactPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
